import { useCallback, useMemo, useRef, useState, type FormEvent } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getImpersonationHeaders } from "@/providers/GraphqlProvider/headers";
import { getAgentChatUrl, getAgentExecuteWriteUrl } from "./constants";
import McpPlaygroundChatView from "./McpPlaygroundChatView";
import { useCanManageInteractionsBool, useCanManagePeople } from "@/hooks/useCapabilities";
import { canConfirmMcpPlaygroundWrite } from "./access";
import { useMcpPlaygroundAuth } from "./useMcpPlaygroundAuth";
import type { McpWriteProposal } from "./writeProposals";
import { confirmedWriteProposalKey, parseWriteProposal, writeProposalKey } from "./writeProposals";
import { messageText, playgroundUiMessage } from "./chatMessages";
import { useMcpPlaygroundAgentChat } from "./useMcpPlaygroundAgentChat";

export default function McpPlaygroundChat() {
  const { getAccessToken } = useCurrentUser();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const { assumedUser } = useAssumeUser();
  const { status: authStatus } = useMcpPlaygroundAuth();
  const canManagePeople = useCanManagePeople();
  const canManageInteractions = useCanManageInteractionsBool();
  const api = getAgentChatUrl();
  const [input, setInput] = useState("");
  const [dismissedProposalKeys, setDismissedProposalKeys] = useState<string[]>([]);
  const [confirmedProposalKeys, setConfirmedProposalKeys] = useState<string[]>([]);

  const authedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await getAccessTokenRef.current();
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);

      Object.entries(getImpersonationHeaders(assumedUser?.id)).forEach(([key, value]) => {
        headers.set(key, value);
      });

      return fetch(input as RequestInfo, {
        ...init,
        headers
      });
    },
    [assumedUser?.id]
  );

  const { messages, sendMessage, isLoading, error, setMessages } = useMcpPlaygroundAgentChat({
    api,
    fetch: authedFetch as typeof fetch
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const text = input.trim();
      if (!text || isLoading) {
        return;
      }

      void sendMessage(text).then((accepted) => {
        if (accepted) {
          setInput("");
        }
      });
    },
    [input, isLoading, sendMessage]
  );

  const pendingWrite = useMemo(() => {
    const assistantMessages = messages.filter((message) => message.role === "assistant");

    // Newest undismissed/unconfirmed proposal wins — a later non-proposal reply must not hide it.
    for (let index = assistantMessages.length - 1; index >= 0; index -= 1) {
      const assistantMessage = assistantMessages[index];
      const proposal = parseWriteProposal(messageText(assistantMessage.content));
      if (!proposal) {
        continue;
      }

      const payloadKey = writeProposalKey(proposal);
      const confirmationKey = confirmedWriteProposalKey(assistantMessage.id, proposal);
      if (dismissedProposalKeys.includes(payloadKey) || confirmedProposalKeys.includes(confirmationKey)) {
        continue;
      }

      return { proposal, confirmationKey };
    }

    return null;
  }, [confirmedProposalKeys, dismissedProposalKeys, messages]);

  const pendingProposal = pendingWrite?.proposal ?? null;

  const handleConfirmWrite = useCallback(
    async (proposal: McpWriteProposal) => {
      if (!canConfirmMcpPlaygroundWrite(proposal.tool, canManagePeople, canManageInteractions)) {
        throw new Error("You do not have permission to confirm this write.");
      }

      const confirmationKey = pendingWrite?.confirmationKey;
      if (!confirmationKey || writeProposalKey(pendingWrite.proposal) !== writeProposalKey(proposal)) {
        throw new Error("This write proposal is no longer pending confirmation.");
      }

      const response = await authedFetch(getAgentExecuteWriteUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          tool: proposal.tool,
          arguments: proposal.arguments,
          proposal_token: proposal.token
        })
      });

      const body = (await response.json()) as { success?: boolean; content?: string; error?: string };
      if (!response.ok || !body.success) {
        throw new Error(body.error || "Write failed");
      }

      setConfirmedProposalKeys((current) => [...current, confirmationKey]);
      setMessages((current) => [...current, playgroundUiMessage("assistant", body.content ?? "Write completed.")]);
    },
    [authedFetch, canManageInteractions, canManagePeople, pendingWrite, setMessages]
  );

  const handleDismissWrite = useCallback((proposal: McpWriteProposal) => {
    setDismissedProposalKeys((current) => [...current, writeProposalKey(proposal)]);
  }, []);

  const canConfirmWrite = pendingProposal
    ? canConfirmMcpPlaygroundWrite(pendingProposal.tool, canManagePeople, canManageInteractions)
    : false;

  return (
    <McpPlaygroundChatView
      messages={messages}
      input={input}
      isLoading={isLoading}
      error={error}
      authStatus={authStatus}
      pendingProposal={pendingProposal}
      canConfirmWrite={canConfirmWrite}
      onInputChange={setInput}
      onSubmit={handleSubmit}
      onConfirmWrite={handleConfirmWrite}
      onDismissWrite={handleDismissWrite}
    />
  );
}
